import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { HeroUIProvider } from "@heroui/react";
import { DispatchHeader, DispatchKPIs, DispatchFilters, DispatchTable, DispatchRequestCard, EmptyRequests } from "@/components/dispatch/DispatchUI";
import { RequestDetails } from "@/components/dispatch/RequestDetails";
import { TeamBookingRequestDialog } from "@/components/dispatch/TeamBookingRequestDialog";

const STATUS_LABEL = { requested: "Awaiting AWB", awb_received: "AWB received", awb_assigned: "Awaiting client", confirmed: "Confirmed" };

function RequestForm({ value, onChange, disabled, saved = [], onDeleteSaved, showSave, orders = [], shipments = [] }) {
  const orderShipments = shipments.filter((s) => !value.order_id || s.order_id === value.order_id || (s.order_ids || []).includes(value.order_id));
  const pick = (id) => {
    const c = saved.find((s) => s.id === id);
    if (c) onChange({ ...value, consignee: c.name, phone: c.phone || "" });
  };
  const NONE = "__none__";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wide">Link to order (optional)</Label>
          <Select value={value.order_id || NONE} onValueChange={(v) => onChange({ ...value, order_id: v === NONE ? "" : v, shipment_id: "" })} disabled={disabled}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="drq-order-select"><SelectValue placeholder="No order" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value={NONE}>No order</SelectItem>
              {orders.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`drq-order-opt-${o.order_number}`}>{o.order_number}{o.client_name ? ` · ${o.client_name}` : ""}{o.patient_name ? ` · ${o.patient_name}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide">Link to shipment (optional)</Label>
          <Select value={value.shipment_id || NONE} onValueChange={(v) => onChange({ ...value, shipment_id: v === NONE ? "" : v })} disabled={disabled}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="drq-shipment-select"><SelectValue placeholder="No shipment" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value={NONE}>No shipment</SelectItem>
              {orderShipments.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`drq-shipment-opt-${s.shipment_number}`}>{s.shipment_number} · {s.order_number}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {saved.length > 0 && !disabled && (
        <div>
          <Label className="text-xs uppercase tracking-wide">Saved consignees</Label>
          <Select onValueChange={pick}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="drq-saved-consignee-select"><SelectValue placeholder="Pick a saved consignee to prefill" /></SelectTrigger>
            <SelectContent>
              {saved.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`drq-saved-consignee-${s.id}`}>{s.name}{s.phone ? ` · ${s.phone}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {onDeleteSaved && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {saved.map((s) => (
                <span key={s.id} className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-2 py-0.5 bg-muted/40" data-testid={`drq-saved-chip-${s.id}`}>
                  {s.name}
                  <button type="button" className="text-muted-foreground hover:text-red-600" onClick={() => onDeleteSaved(s.id)} aria-label={`Remove ${s.name}`} data-testid={`drq-saved-delete-${s.id}`}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div>
        <Label className="text-xs uppercase tracking-wide">Consignee</Label>
        <Textarea rows={3} className="mt-1.5 rounded-sm" disabled={disabled} value={value.consignee} onChange={(e) => onChange({ ...value, consignee: e.target.value })} placeholder="Consignee name & address" data-testid="drq-consignee-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide">Phone number</Label>
        <Input type="tel" className="mt-1.5 rounded-sm" disabled={disabled} value={value.phone || ""} onChange={(e) => onChange({ ...value, phone: e.target.value })} data-testid="drq-phone-input" />
      </div>
      {showSave && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={!!value.save_consignee} onCheckedChange={(v) => onChange({ ...value, save_consignee: !!v })} data-testid="drq-save-consignee-checkbox" />
          Save this consignee for next time
        </label>
      )}
      <div>
        <Label className="text-xs uppercase tracking-wide">Number of boxes</Label>
        <Input type="number" min="1" className="mt-1.5 rounded-sm" disabled={disabled} value={value.boxes} onChange={(e) => onChange({ ...value, boxes: e.target.value })} data-testid="drq-boxes-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide">Notes</Label>
        <Textarea rows={3} className="mt-1.5 rounded-sm" disabled={disabled} value={value.notes} onChange={(e) => onChange({ ...value, notes: e.target.value })} data-testid="drq-notes-input" />
      </div>
    </div>
  );
}

export default function DispatchRequests() {
  const { user } = useAuth();
  const isTeam = user?.role === "admin" || user?.role === "team";
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const presetShipmentId = params.get("shipment") || "";
  useEffect(() => { if (params.get("new") === "1" && isTeam) setCreateOpen(true); }, [params, isTeam]);
  const [saved, setSaved] = useState([]);
  const [orders, setOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [edit, setEdit] = useState(null);
  const [awbNumber, setAwbNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/dispatch-requests").then((r) => setRows(r.data)).catch(() => {});
  const loadSaved = () => {
    if (!isTeam) return;
    api.get("/consignees").then((r) => setSaved(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data.filter((o) => o.status === "approved"))).catch(() => {});
    api.get("/shipments").then((r) => setShipments(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadSaved(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const deleteSaved = async (cid) => {
    try { await api.delete(`/consignees/${cid}`); toast.success("Saved consignee removed"); loadSaved(); } catch (e) { fail(e); }
  };
  const saveCurrentConsignee = async (v) => {
    if (!v.consignee?.trim()) return toast.error("Enter a consignee name first");
    try { await api.post("/consignees", { name: v.consignee, phone: v.phone || "" }); toast.success("Consignee saved"); loadSaved(); } catch (e) { fail(e); }
  };

  const openView = (r) => { setViewing(r); setEdit(null); setAwbNumber(r.awb_number || ""); };
  const refreshView = async (id) => { const { data } = await api.get(`/dispatch-requests/${id}`); setViewing(data); load(); };
  const fail = (e) => toast.error(e?.response?.data?.detail || "Failed");


  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/dispatch-requests/${viewing.id}`, { consignee: edit.consignee, phone: edit.phone || "", boxes: Number(edit.boxes), notes: edit.notes, order_id: edit.order_id || "", shipment_id: edit.shipment_id || "" });
      toast.success("Request updated"); setEdit(null); refreshView(viewing.id);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const saveAwbNumber = async () => {
    try { await api.patch(`/dispatch-requests/${viewing.id}`, { awb_number: awbNumber }); toast.success("AWB number saved"); refreshView(viewing.id); }
    catch (e) { fail(e); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/dispatch-requests/${viewing.id}/awb`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("AWB uploaded"); refreshView(viewing.id);
    } catch (e) { fail(e); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAwb = async (fid) => {
    try { await api.delete(`/dispatch-requests/${viewing.id}/awb/${fid}`); toast.success("AWB removed"); refreshView(viewing.id); } catch (e) { fail(e); }
  };

  const confirm = async () => {
    try { await api.post(`/dispatch-requests/${viewing.id}/confirm`); toast.success("Booking confirmed"); refreshView(viewing.id); } catch (e) { fail(e); }
  };
  const assignAwb = async () => {
    try { await api.post(`/dispatch-requests/${viewing.id}/assign-awb`); toast.success("AWB assigned to shipment — client asked to confirm"); refreshView(viewing.id); load(); } catch (e) { fail(e); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${viewing.request_number}? This cannot be undone.`)) return;
    try { await api.delete(`/dispatch-requests/${viewing.id}`); toast.success("Request deleted"); setViewing(null); load(); } catch (e) { fail(e); }
  };

  const locked = viewing?.status === "confirmed";
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const needle = q.trim().toLowerCase();
  const filtered = rows.filter((r) => (statusFilter === "all" || r.status === statusFilter) &&
    (!needle || [r.request_number, r.consignee, r.phone, r.order_number, r.shipment_number, r.awb_number, r.notes].some((v) => String(v || "").toLowerCase().includes(needle))));

  const [sort, setSort] = useState("newest");
  const counts = { all: rows.length, requested: rows.filter((r) => r.status === "requested").length, awb_received: rows.filter((r) => r.status === "awb_received").length, awb_assigned: rows.filter((r) => r.status === "awb_assigned").length, confirmed: rows.filter((r) => r.status === "confirmed").length };
  const sorted = [...filtered].sort((a, b) => (sort === "newest" ? 1 : -1) * String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const openFor = (r, action) => { openView(r); setInitialTab(action === "awb" ? "documents" : "overview"); };
  const [initialTab, setInitialTab] = useState("overview");

  return (
    <HeroUIProvider>
    <div className="space-y-5 pb-4" data-testid="dispatch-requests-page">
      <DispatchHeader onNew={isTeam ? () => setCreateOpen(true) : null} />
      <DispatchKPIs counts={counts} onPick={setStatusFilter} />
      <DispatchFilters q={q} onQ={setQ} status={statusFilter} onStatus={setStatusFilter} counts={counts} sort={sort} onSort={setSort} shown={sorted.length} />

      <div className="md:hidden space-y-3" data-testid="drq-mobile-list">
        {sorted.length === 0 ? <EmptyRequests any={rows.length > 0} /> : sorted.map((r) => <DispatchRequestCard key={r.id} r={r} onOpen={openFor} canAct={isTeam} />)}
      </div>
      <div className="hidden md:block">
        <DispatchTable rows={sorted} any={rows.length > 0} onOpen={openFor} />
      </div>

      <TeamBookingRequestDialog open={createOpen} presetShipmentId={presetShipmentId} onClose={() => { setCreateOpen(false); if (params.get("new")) setParams({}, { replace: true }); }} onCreated={() => { load(); loadSaved(); }} saved={saved} orders={orders} shipments={shipments} />

      <RequestDetails
        viewing={viewing} onClose={() => setViewing(null)} isTeam={isTeam} locked={locked} busy={busy} initialTab={initialTab}
        edit={edit} setEdit={setEdit} saveEdit={saveEdit} saveCurrentConsignee={saveCurrentConsignee} saved={saved} orders={orders} shipments={shipments} RequestForm={RequestForm}
        awbNumber={awbNumber} setAwbNumber={setAwbNumber} saveAwbNumber={saveAwbNumber} fileRef={fileRef} upload={upload} removeAwb={removeAwb} confirm={confirm} assignAwb={assignAwb} remove={remove}
      />
    </div>
    </HeroUIProvider>
  );
}
